import { Button, Col, Divider, Flex, Input, Row, Skeleton, Typography } from "antd";
import { type MouseEvent, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router";
import Page from "~Common/Page";
import PermissionCheck from "~Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import InviteNotifyModal from "~pages/Users/components/InviteNotifyModal/InviteNotifyModal.tsx";
import UserPermissionsDataTable from "~pages/Users/components/UserPermissionsDataTable/UserPermissionsDataTable.tsx";
import type { PermissionProducts } from "~pages/Users/enums/permissionProducts.ts";
import { usePermissionUserService } from "~pages/Users/hooks/usePermissionUserService.ts";
import type { User } from "~types/user.ts";
import { userRolesConfig } from "../constants/userRolesConfig";
import { useGetPermissionUser } from "../hooks";
import type { UserPermissionTransformed } from "../types";

const PermissionUserDetailsPage = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId, sport, organizationId } = useParams();
  const { userRole: routeUserRole } = location?.state || {};
  const routeProduct = routeUserRole ? userRolesConfig[routeUserRole]?.product : undefined;

  const { data: userDetails, isLoading, refetch } = useGetPermissionUser();
  const { deleteUserPermission } = usePermissionUserService();
  const [inviteNotifyModalUser, setInviteNotifyModalUser] = useState<User | null>(null);
  const [activeProductTab, setActiveProductTab] = useState<PermissionProducts | undefined>(undefined);

  const goBackToUserList = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/permissions`);

  const onEditPermissionClick = (userPermission: UserPermissionTransformed) => navigate(`permission/${userPermission.permissionId}`);

  const onDeletePermissionClick = async (userPermission: UserPermissionTransformed) => {
    await deleteUserPermission(userPermission.permissionId);
  };

  const onInviteNotify = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setInviteNotifyModalUser(userDetails ?? null);
    },
    [userDetails],
  );

  let pageTitle = formatMessage({ id: "permissions.user.actions.edit" });
  if (userDetails?.name) {
    pageTitle += ` — ${userDetails.name}`;
  }

  return (
    <Page title={pageTitle} subtitle={formatMessage({ id: "permissions.user.actions.edit.subtitle" })} onBack={goBackToUserList}>
      <InviteNotifyModal user={inviteNotifyModalUser} preferredProduct={activeProductTab} onClose={() => setInviteNotifyModalUser(null)} />
      <Flex justify="space-between" vertical={true} gap={8}>
        <Flex justify="space-between" align="center">
          <Typography.Title level={4}>{formatMessage({ id: "basic.details" })}</Typography.Title>
          <PermissionCheck permissionKeys={[PermissionNames.PERMISSIONS]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Button
              disabled={isLoading || !userDetails}
              onClick={onInviteNotify}
              icon={<i className={`fas fa-${userDetails?.isPending ? "paper-plane" : "bell"}`} style={{ marginRight: "5px" }} />}
              type="primary"
            >
              {formatMessage({ id: userDetails?.isPending ? "permissions.user.actions.invite" : "permissions.user.actions.notify" })}
            </Button>
          </PermissionCheck>
        </Flex>
        <Row>
          <Col span={8}>
            <Typography.Text>{formatMessage({ id: "permissions.user.name" })}</Typography.Text>
            {isLoading ? <Skeleton.Input block active size="large" /> : <Input size="large" disabled value={userDetails?.name} />}
          </Col>
        </Row>
        <Row>
          <Col span={8}>
            <Typography.Text>{formatMessage({ id: "email" })}</Typography.Text>
            {isLoading ? <Skeleton.Input block active size="large" /> : <Input size="large" disabled value={userDetails?.email} />}
          </Col>
        </Row>
        <Divider />
        <Flex justify="space-between" align="center">
          <Typography.Title level={5}>{formatMessage({ id: "permissions.user.roles.assigned" })}</Typography.Title>
          <PermissionCheck permissionKeys={[PermissionNames.PERMISSIONS]} permissionOptions={[PermissionOperations.CREATE]}>
            <Button
              disabled={isLoading}
              onClick={() => navigate("permission")}
              icon={<i className="fas fa-plus" style={{ marginRight: "5px" }} />}
              type="primary"
            >
              {formatMessage({ id: "permissions.user.role.actions.add" })}
            </Button>
          </PermissionCheck>
        </Flex>
        <UserPermissionsDataTable
          permissions={userDetails?.permissions}
          permissionScopeResources={userDetails?.resources || {}}
          isLoading={isLoading}
          readOnly={false}
          onEditPermissionClick={onEditPermissionClick}
          onDeletePermissionClick={onDeletePermissionClick}
          onRefresh={async () => {
            await refetch();
          }}
          onProductTabChange={setActiveProductTab}
          initialProduct={routeProduct}
          initialUserRoles={routeUserRole ? [routeUserRole] : undefined}
          localStorageUniqueId={`permissionUserDetailsPage`}
        />
      </Flex>
    </Page>
  );
};

export default PermissionUserDetailsPage;
